if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.cart = this.getCart();
        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.variantIdInput && !this.submitButton?.hasAttribute('disabled')) {
          this.variantIdInput.disabled = false;
        }

        if (this.form && this.form.dataset.productFormBound !== 'true') {
          this.form.dataset.productFormBound = 'true';
          this.form.addEventListener('submit', this.onSubmitHandler);
        }

        if (document.querySelector('cart-drawer')) {
          this.submitButton?.setAttribute('aria-haspopup', 'dialog');
        }

        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.onPreorderKeydown = this.onPreorderKeydown.bind(this);
        document.addEventListener('change', this.onQuantityChange);
        this.bindPreorderModal();
        this.updateButtonPrice();

        // Quantity is reset programmatically after add-to-cart and variant change (no native change event).
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
            this.updateButtonPrice();
          });
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const productInfo = this.closest('product-info');
            if (!productInfo) return;
            if (event.data?.sectionId && event.data.sectionId !== productInfo.sectionId) return;

            const cents = event.data?.variant?.price;
            if (cents != null && !Number.isNaN(Number(cents))) {
              this.setVariantPrice(cents);
            } else {
              this.updateButtonPrice();
            }
          });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onQuantityChange);
        document.removeEventListener('keydown', this.onPreorderKeydown);
        this.quantityUpdateUnsubscriber?.();
        this.variantChangeUnsubscriber?.();
      }

      bindPreorderModal() {
        this.preorderModal = document.querySelector(this.dataset.preorderModal || '');
        this.preorderNotice = this.querySelector('[data-preorder-notice]');

        this.querySelectorAll('[data-preorder-learn-more]').forEach((el) => {
          el.addEventListener('click', (event) => {
            event.preventDefault();
            this.openPreorderModal();
          });
        });

        if (!this.preorderModal) return;

        this.preorderConfirmButton = this.preorderModal.querySelector('[data-preorder-confirm]');
        this.preorderConfirmLabel = this.preorderModal.querySelector('[data-preorder-confirm-label]');

        this.preorderModal.querySelectorAll('[data-preorder-close]').forEach((el) => {
          el.addEventListener('click', () => this.closePreorderModal());
        });

        this.preorderConfirmButton?.addEventListener('click', () => this.confirmPreorder());
      }

      onPreorderKeydown(event) {
        if (event.key === 'Escape') this.closePreorderModal();
      }

      openPreorderModal() {
        if (!this.preorderModal) {
          this.preorderModal = document.querySelector(this.dataset.preorderModal || '');
        }
        if (!this.preorderModal) return;

        if (!this.preorderModal.dataset.moved) {
          this.preorderModal.dataset.moved = 'true';
          document.body.appendChild(this.preorderModal);
        }

        this.preorderConfirmButton =
          this.preorderConfirmButton || this.preorderModal.querySelector('[data-preorder-confirm]');
        this.preorderConfirmLabel =
          this.preorderConfirmLabel || this.preorderModal.querySelector('[data-preorder-confirm-label]');

        if (this.preorderConfirmLabel) {
          this.preorderConfirmLabel.textContent = this.getPreorderLabel();
        }

        this.preorderModal.hidden = false;
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onPreorderKeydown);
        this.preorderConfirmButton?.focus();
      }

      closePreorderModal() {
        if (!this.preorderModal || this.preorderModal.hidden) return;
        this.preorderModal.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onPreorderKeydown);
        this.submitButton?.focus();
      }

      confirmPreorder() {
        if (this.preorderConfirmButton?.getAttribute('aria-disabled') === 'true') return;

        this.preorderConfirmButton.setAttribute('aria-disabled', 'true');
        this.preorderConfirmButton.classList.add('loading');
        this.preorderConfirmButton.querySelector('.loading__spinner')?.classList.remove('hidden');
        this.closePreorderModal();
        this.submitAddToCart();
      }

      isPreorderButton() {
        return this.submitButton?.getAttribute('data-preorder') === 'true';
      }

      isProductPreorder() {
        return this.dataset.productPreorder === 'true';
      }

      getCart() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      getCartSectionIds() {
        const cart = this.getCart();
        if (typeof cart?.getSectionsToRender === 'function') {
          return cart.getSectionsToRender().map((section) => section.id);
        }
        if (document.querySelector('cart-drawer')) return ['cart-drawer', 'cart-icon-bubble'];
        if (document.querySelector('cart-notification')) {
          return ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
        }
        return [];
      }

      togglePreorderNotice(show) {
        this.preorderNotice = this.preorderNotice || this.querySelector('[data-preorder-notice]');
        if (!this.preorderNotice) return;
        this.preorderNotice.hidden = !show;
      }

      getVariantAvailability(variant) {
        if (!variant) return null;
        if (variant.inventory_quantity != null) return variant;

        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        let availability = [];
        try {
          availability = JSON.parse(variantSelects?.querySelector('[data-variant-availability]')?.textContent || '[]');
        } catch (e) {
          return variant;
        }
        const match = availability.find((item) => String(item.id) === String(variant.id));
        return match ? { ...variant, ...match } : variant;
      }

      isVariantUnavailable(variant) {
        const detail = this.getVariantAvailability(variant);
        if (!detail) return true;
        if (detail.available === false) return true;
        if (detail.inventory_management && detail.inventory_quantity != null && Number(detail.inventory_quantity) <= 0) {
          return true;
        }
        return false;
      }

      isVariantPreorder(variant) {
        return Boolean(variant) && this.isProductPreorder();
      }

      getQuantity() {
        const quantityInput = this.closest('product-info')?.querySelector('.quantity__input');
        const parsed = parseInt(quantityInput?.value, 10);
        if (Number.isNaN(parsed) || parsed < 1) return 1;
        return parsed;
      }

      getPreorderLabel(cents) {
        const base = window.variantStrings.preorder || 'Pre-order';
        if (this.dataset.showPrice !== 'true') return base;

        const unitPrice = cents != null ? parseInt(cents, 10) : parseInt(this.dataset.variantPrice, 10);
        if (Number.isNaN(unitPrice)) return base;

        const money = this.formatMoney(unitPrice * this.getQuantity(), this.dataset.moneyFormat);
        return `${base} - ${money}`;
      }

      setPreorderButton(cents, variant) {
        if (!this.submitButton) return;

        if (cents != null && !Number.isNaN(Number(cents))) {
          this.dataset.variantPrice = String(cents);
        }

        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.setAttribute('data-preorder', 'true');
        if (this.submitButtonText) {
          this.submitButtonText.textContent = this.getPreorderLabel(cents);
        }
        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }
        this.togglePreorderNotice(true);
      }

      onQuantityChange(event) {
        if (!event.target.classList?.contains('quantity__input')) return;
        if (!this.closest('product-info')?.contains(event.target)) return;
        this.updateButtonPrice();
      }

      formatMoney(cents, format) {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, format);
        }

        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const formatString = format || '${{amount}}';

        function formatWithDelimiters(number, precision, thousands, decimal) {
          precision = precision == null ? 2 : precision;
          thousands = thousands || ',';
          decimal = decimal || '.';

          if (isNaN(number) || number == null) return '0';

          number = (number / 100.0).toFixed(precision);
          const parts = number.split('.');
          const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
          const centsAmount = parts[1] ? decimal + parts[1] : '';
          return dollarsAmount + centsAmount;
        }

        switch (formatString.match(placeholderRegex)?.[1]) {
          case 'amount':
            return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2));
          case 'amount_no_decimals':
            return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 0));
          case 'amount_with_comma_separator':
            return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2, '.', ','));
          case 'amount_no_decimals_with_comma_separator':
            return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 0, '.', ','));
          case 'amount_with_apostrophe_separator':
            return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2, "'", '.'));
          default:
            return formatString.replace(placeholderRegex, (Number(cents) / 100).toFixed(2));
        }
      }

      updateButtonPrice() {
        if (!this.submitButtonText) return;
        if (this.submitButton?.hasAttribute('disabled') && !this.isPreorderButton()) return;

        if (this.isPreorderButton()) {
          this.submitButtonText.textContent = this.getPreorderLabel();
          return;
        }

        if (this.dataset.showPrice !== 'true') return;

        const unitPrice = parseInt(this.dataset.variantPrice, 10);
        if (Number.isNaN(unitPrice)) return;

        const total = unitPrice * this.getQuantity();
        const money = this.formatMoney(total, this.dataset.moneyFormat);

        this.submitButtonText.textContent = `${window.variantStrings.addToCart} - ${money}`;
      }

      syncVariantFromOptions() {
        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) return;

        const match = this.getSelectedVariant();
        const input = this.variantIdInput;
        if (!input) return;

        const hasSelectedOptions = Boolean(
          variantSelects.querySelector('input:checked') ||
            variantSelects.querySelector('.product-form__input--dropdown select')?.selectedOptions?.[0] ||
            variantSelects.querySelector('select.select__select')?.selectedOptions?.[0]
        );
        if (!hasSelectedOptions) return;

        if (match?.id) {
          input.value = String(match.id);
          if (this.isVariantPreorder(match)) {
            input.disabled = false;
            this.setPreorderButton(match.price, match);
          } else if (match.available === false) {
            input.disabled = true;
            this.toggleSubmitButton(true, window.variantStrings.outOfStock || window.variantStrings.soldOut);
          } else {
            input.disabled = false;
            if (this.submitButton?.hasAttribute('disabled') || this.isPreorderButton()) {
              this.toggleSubmitButton(false);
            }
            this.setVariantPrice(match.price);
          }
        } else {
          input.value = '';
          input.disabled = true;
          this.toggleSubmitButton(true, window.variantStrings.unavailable);
        }
      }

      getSelectedVariant() {
        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) return null;

        let variants = [];
        try {
          variants = JSON.parse(variantSelects.querySelector('[data-product-variants]')?.textContent || '[]');
        } catch (e) {
          return null;
        }
        if (!Array.isArray(variants) || !variants.length) return null;

        const selectedOptions = [];
        variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          if (checked) selectedOptions.push(checked.value);
        });
        variantSelects.querySelectorAll('.product-form__input--dropdown select, select.select__select').forEach((select) => {
          if (select.selectedOptions?.[0]) selectedOptions.push(select.selectedOptions[0].value);
        });

        if (!selectedOptions.length) return null;

        return (
          variants.find((variant) => {
            const opts = [variant.option1, variant.option2, variant.option3].filter(
              (option) => option != null && option !== ''
            );
            return (
              opts.length === selectedOptions.length &&
              opts.every((option, index) => option === selectedOptions[index])
            );
          }) || null
        );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute('aria-disabled') === 'true' ||
          this.submitButton.hasAttribute('disabled')
        ) {
          return;
        }

        this.submitAddToCart(evt);
      }

      submitAddToCart(evt) {
        // Always read/sync variant ID from selected options before submit
        this.syncVariantFromOptions();
        this.cart = this.getCart();

        const variantInput = this.variantIdInput;
        const variantId = String(variantInput?.value || '').trim();

        if (!variantId || variantId === 'null' || variantId === 'undefined') {
          this.handleErrorMessage(window.variantStrings?.unavailable || 'Unavailable');
          this.toggleSubmitButton(true, window.variantStrings?.unavailable);
          return;
        }

        const quantityInput = this.closest('product-info')?.querySelector('.quantity__input');
        const quantity = Math.max(
          parseInt(quantityInput?.value || this.form.querySelector('[name="quantity"]')?.value, 10) || 1,
          1
        );

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        if (variantInput) variantInput.disabled = false;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = this.form ? new FormData(this.form) : new FormData();
        formData.set('id', variantId);
        formData.set('quantity', String(quantity));
        formData.set('sections_url', window.location.pathname);

        const sectionIds = this.getCartSectionIds();
        if (sectionIds.length) {
          formData.set('sections', sectionIds.join(','));
        }
        this.cart?.setActiveElement?.(document.activeElement);
        config.body = formData;

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        const addUrl = window.routes?.cart_add_url || routes.cart_add_url;
        const addJsUrl = addUrl.endsWith('.js') ? addUrl : `${addUrl}.js`;

        fetch(addJsUrl, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.cart = this.getCart();
            this.resolveCartLinesUpdate(linesUpdateDeferred);

            if (!this.cart) {
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const paintCart = () => {
              if (typeof this.cart.renderContents === 'function') {
                this.cart.renderContents(response);
              }
              this.cart.open?.();
            };
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', paintCart);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', paintCart);
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.preorderConfirmButton?.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              this.preorderConfirmButton?.removeAttribute('aria-disabled');
            }
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            this.preorderConfirmButton?.querySelector('.loading__spinner')?.classList.add('hidden');

            if (this.isPreorderButton() && variantInput) {
              variantInput.disabled = false;
            }

            if (evt) CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text, options = {}) {
        if (!this.submitButton) return;

        if (disable && options.preorder) {
          this.setPreorderButton(options.price, options.variant || this.getSelectedVariant());
          return;
        }

        this.submitButton.removeAttribute('data-preorder');
        this.submitButton.removeAttribute('aria-controls');
        if (!this.isProductPreorder() || !disable || text) {
          this.togglePreorderNotice(false);
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
          if (this.variantIdInput) this.variantIdInput.disabled = true;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = text || window.variantStrings.addToCart;
          }
          if (this.variantIdInput) this.variantIdInput.disabled = false;
          this.updateButtonPrice();
        }
      }

      setVariantPrice(cents) {
        if (cents == null || Number.isNaN(Number(cents))) return;
        this.dataset.variantPrice = String(cents);
        this.updateButtonPrice();
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form?.querySelector('[name="id"]');
      }
    }
  );
}
